import client from './client';

// ─── Types ───────────────────────────────────────────────────────────

export enum FormFieldType {
  TEXT = 'text',
  SELECT = 'select',
  CHECKBOX = 'checkbox',
  RADIO = 'radio',
  FILE = 'file',
}

export enum FormSubmissionStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
}

export interface FormFieldDto {
  type: FormFieldType;
  label: string;
  description?: string;
  required: boolean;
  options?: string[];
  allowMultiple?: boolean;
  allowCustomResponse?: boolean;
  order?: number;
}

export interface GetFormFieldDto extends FormFieldDto {
  id: string;
}

export interface CreateFormDto {
  name: string;
  description?: string;
  fields: FormFieldDto[];
}

export interface UpdateFormDto {
  name?: string;
  description?: string;
  fields?: FormFieldDto[];
}

export interface GetFormDto {
  id: string;
  name: string;
  description?: string;
  organizationId: string;
  createdBy: string;
  fields: GetFormFieldDto[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FieldResponseDto {
  fieldId: string;
  value: any;
  customValue?: string;
}

export interface CreateFormSubmissionDto {
  formId: string;
  visitId: string;
  responses: FieldResponseDto[];
  status: FormSubmissionStatus;
}

export interface UpdateFormSubmissionDto {
  responses?: FieldResponseDto[];
  status?: FormSubmissionStatus;
}

export interface GetFieldResponseDto {
  fieldId: string;
  value: any;
  customValue?: string | null;
}

export interface GetFormSubmissionDto {
  id: string;
  formId: string;
  visitId: string;
  userId: string;
  organizationId: string;
  responses: GetFieldResponseDto[];
  status: FormSubmissionStatus;
  createdAt: string;
  updatedAt: string;
}

export interface RequiredFormInfo {
  formId: string;
  formName: string;
  submissionStatus: 'not_started' | 'draft' | 'submitted';
}

// ─── API Client ──────────────────────────────────────────────────────

const FORMS_BASE = '/api/forms';

export const formApi = {
  // Form CRUD
  createForm: (data: CreateFormDto) =>
    client.post<GetFormDto>(FORMS_BASE, data),

  getFormsByOrganization: (organizationId: string) =>
    client.get<GetFormDto[]>(`${FORMS_BASE}/organization/${organizationId}`),

  getFormById: (formId: string) =>
    client.get<GetFormDto>(`${FORMS_BASE}/${formId}`),

  updateForm: (formId: string, data: UpdateFormDto) =>
    client.put<GetFormDto>(`${FORMS_BASE}/${formId}`, data),

  deleteForm: (formId: string) =>
    client.delete<{ success: boolean }>(`${FORMS_BASE}/${formId}`),

  // Form Submissions
  upsertSubmission: (data: CreateFormSubmissionDto) =>
    client.post<GetFormSubmissionDto>(`${FORMS_BASE}/submissions`, data),

  getSubmissionsByVisit: (visitId: string) =>
    client.get<GetFormSubmissionDto[]>(`${FORMS_BASE}/submissions/visit/${visitId}`),

  getSubmissionById: (submissionId: string) =>
    client.get<GetFormSubmissionDto>(`${FORMS_BASE}/submissions/${submissionId}`),

  updateSubmission: (submissionId: string, data: UpdateFormSubmissionDto) =>
    client.put<GetFormSubmissionDto>(`${FORMS_BASE}/submissions/${submissionId}`, data),

  // Service-Form association (on specialization controller)
  getServiceForms: (serviceId: string) =>
    client.get<{ formIds: string[] }>(`/api/specialization/service/${serviceId}/forms`),

  updateServiceForms: (serviceId: string, formIds: string[]) =>
    client.put<{ id: string; formIds: string[]; message: string }>(
      `/api/specialization/service/${serviceId}/forms`,
      { formIds },
    ),
};
